import { useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { InputField, TextareaField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import type { RevisedAccountsDialogData, RevisedAccountsView } from '@/core/models/report-options.models';
import { notifyError } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { validateForm } from './common';

/** Angular ReplacementDialogComponent: the reasons and amendments of "Revision by Replacement". */
export function ReplacementDialog({
  data,
  dialogRef,
}: DialogComponentProps<RevisedAccountsDialogData, RevisedAccountsView>) {
  const view = data?.view;
  const [replacementReason, setReplacementReason] = useState(view?.replacementReason ?? '');
  const [replacementAmendments, setReplacementAmendments] = useState(view?.replacementAmendments ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Validators.required (empty string) and Validators.maxLength.
  const reasonValid = replacementReason.trim().length > 0 && replacementReason.length <= 20000;
  const amendmentsValid = replacementAmendments.trim().length > 0 && replacementAmendments.length <= 2000;
  const touch = (name: string) => setTouched((current) => new Set(current).add(name));

  const onSave = () => {
    if (!view || isSaving) return;
    // Both values are trimmed before they are validated, so a blank one fails `required`.
    const reason = `${replacementReason ?? ''}`.trim();
    const amendments = `${replacementAmendments ?? ''}`.trim();
    setReplacementReason(reason);
    setReplacementAmendments(amendments);
    const valid = reason.length > 0 && reason.length <= 20000 && amendments.length > 0 && amendments.length <= 2000;
    if (!validateForm(valid)) {
      setTouched(new Set(['replacementReason', 'replacementAmendments']));
      return;
    }
    setIsSaving(true);
    reportOptionsService
      .saveReplacement({
        clientId: view.clientId,
        accountingPeriodId: view.accountingPeriodId,
        replacementReason: reason,
        replacementAmendments: amendments,
      })
      .then(
        (saved) => dialogRef.close(saved),
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSaving(false));
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Revision by Replacement</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="pl-5 pr-5">
            <div className="form-field textarea">
              <p className="fw-bold">
                Enter the reason the original accounts did not comply with the Companies Act 2006{' '}
                <span className="text-red">*</span>
              </p>
              <TextareaField
                fieldClassName="wp-100"
                required
                maxLength={20000}
                value={replacementReason}
                invalid={touched.has('replacementReason') && !reasonValid}
                onChange={(event) => setReplacementReason(event.target.value)}
                onBlur={() => touch('replacementReason')}
              />
            </div>
            <div className="form-field">
              <p className="fw-bold">
                Enter details of significant amendments you have made to correct the defects{' '}
                <span className="text-red">*</span>
              </p>
              <InputField
                fieldClassName="wp-100"
                required
                maxLength={2000}
                value={replacementAmendments}
                invalid={touched.has('replacementAmendments') && !amendmentsValid}
                onChange={(event) => setReplacementAmendments(event.target.value)}
                onBlur={() => touch('replacementAmendments')}
              />
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
